import {
  CampaignHealthLevel,
  getCampaignHealthLabel,
  getCampaignHealthLevel,
  getOperationalScoreEmoji,
  getReadinessLabel,
  OPS_COLORS,
} from '../constants/operationsDashboardColors';

export interface CampaignSummaryItem {
  campaignId: string;
  campaignName: string;
  scheduled: number;
  present: number;
  late: number;
  absent: number;
  covered: number;
  uncovered: number;
  coverageReceived?: number;
  coverageGiven?: number;
  availableAgents?: number;
  operationalScore: number;
  coveragePercent: number;
  operationalScoreLabel?: string;
  operationalScoreLevel?: string;
}

export interface ActionQueueItem {
  id: string;
  priority: 'high' | 'medium' | 'low';
  title: string;
  campaignName: string;
  campaignId?: string;
  rowId?: string;
  actionType: 'assign_coverage' | 'review_absence' | 'staffing_threshold' | 'coverage_pending';
}

export function buildReadinessTooltip(params: {
  assignmentPositionsRequired: number;
  availableAtPosition: number;
  coveredPositions: number;
  uncoveredPositions: number;
}): string {
  const lines = [
    'Weighted average of campaign operational scores across assignment positions.',
    `${params.assignmentPositionsRequired} positions · ${params.availableAtPosition} staffed · ${formatStaffingValue(params.coveredPositions)} covered`,
  ];
  if (params.uncoveredPositions > 0) {
    lines.push(`${formatStaffingValue(params.uncoveredPositions)} position(s) still uncovered.`);
  }
  lines.push('Based on positions, not unique employees (split schedules count as multiple positions).');
  return lines.join('\n');
}

export function computeWeightedOperationalReadiness(campaignSummary: CampaignSummaryItem[]): {
  score: number;
  label: string;
  emoji: string;
  requiredStaff: number;
  availableStaff: number;
  coveredPositions: number;
  uncoveredPositions: number;
} {
  const requiredStaff = campaignSummary.reduce((sum, c) => sum + c.scheduled, 0);
  const availableStaff = campaignSummary.reduce((sum, c) => sum + (c.availableAgents ?? 0), 0);
  const coveredPositions = campaignSummary.reduce((sum, c) => sum + c.covered, 0);
  const uncoveredPositions = campaignSummary.reduce((sum, c) => sum + c.uncovered, 0);

  const score = requiredStaff > 0
    ? Math.round(
      campaignSummary.reduce((sum, c) => sum + c.operationalScore * c.scheduled, 0) / requiredStaff,
    )
    : 100;

  return {
    score,
    label: getReadinessLabel(score),
    emoji: getOperationalScoreEmoji(score),
    requiredStaff,
    availableStaff,
    coveredPositions,
    uncoveredPositions,
  };
}

export function formatStaffingValue(value: number): string {
  if (Number.isInteger(value)) return String(value);
  return value.toFixed(1);
}

/** Supervisor-facing counts — never expose fractional staffing values. */
export function formatAgentCount(count: number, noneLabel = 'None'): string {
  const rounded = Math.max(0, Math.round(count));
  if (rounded === 0) return noneLabel;
  return `${rounded} Agent${rounded === 1 ? '' : 's'}`;
}

export function formatAgentsNeeded(count: number): string {
  const rounded = Math.ceil(Math.max(0, count));
  if (rounded === 0) return 'Fully Staffed';
  return `Needs ${rounded} Agent${rounded === 1 ? '' : 's'}`;
}

export interface CampaignOperationsMetrics {
  required: number;
  working: number;
  coverageCount: number;
  coverageLabel: string;
  stillNeeded: number;
  staffingPercent: number;
  workingLabel: string;
  hasPartialCoverage: boolean;
  partialCoverageDetail?: string;
}

export function getCampaignOperationsMetrics(
  campaign: CampaignSummaryItem,
  campaignRows: any[] = [],
): CampaignOperationsMetrics {
  const required = campaign.scheduled;
  const working = campaign.present + campaign.late;
  const stillNeeded = campaign.uncovered;
  const partialRows = campaignRows.filter((r) => r.coverageStatus === 'PARTIALLY_COVERED');
  const fullRows = campaignRows.filter((r) => r.coverageStatus === 'COVERED');
  const coverageCount = fullRows.length + partialRows.length;
  const hasPartialCoverage = partialRows.length > 0;

  let coverageLabel = 'None';
  let partialCoverageDetail: string | undefined;

  if (hasPartialCoverage && fullRows.length === 0) {
    coverageLabel = 'Partial Coverage';
    const row = partialRows[0];
    const mins = row?.coverageDurationMinutes;
    if (mins && mins > 0) {
      partialCoverageDetail = `${Math.round(mins)} Minutes Covered`;
    } else if (row?.scheduledStartTime && row?.scheduledEndTime) {
      partialCoverageDetail = 'Partial shift covered';
    } else {
      const pct = Math.round((campaign.covered % 1) * 100) || Math.round(campaign.covered * 100);
      if (pct > 0 && pct < 100) partialCoverageDetail = `${pct}% of Shift Covered`;
    }
  } else if (coverageCount > 0) {
    coverageLabel = formatAgentCount(coverageCount);
  }

  const staffing = getCampaignStaffingStatus(campaign);
  const staffingPercent = required > 0 ? Math.round(staffing.staffingPercent) : 0;
  const workingLabel = required > 0
    ? `${working} of ${required} Agents Working`
    : 'Not Scheduled Today';

  return {
    required,
    working,
    coverageCount,
    coverageLabel,
    stillNeeded,
    staffingPercent,
    workingLabel,
    hasPartialCoverage,
    partialCoverageDetail,
  };
}

export function isCampaignFullyHealthy(campaign: CampaignSummaryItem): boolean {
  if (campaign.scheduled <= 0) return true;
  return getCampaignHealthLevel(campaign.operationalScore, campaign.scheduled) === 'healthy'
    && getCampaignStaffingStatus(campaign).openPositions === 0;
}

export function areAllCampaignsHealthy(campaigns: CampaignSummaryItem[]): boolean {
  return campaigns.length > 0 && campaigns.every(isCampaignFullyHealthy);
}

export function getCampaignsNeedingAttention(campaigns: CampaignSummaryItem[]): CampaignSummaryItem[] {
  return campaigns.filter((campaign) => !isCampaignFullyHealthy(campaign));
}

export function getBorrowDisplayLabel(
  campaign: CampaignSummaryItem,
  canLend: number,
): string {
  const staffing = getCampaignStaffingStatus(campaign);
  if (campaign.scheduled <= 0) return 'Not Scheduled';
  if (staffing.openPositions > 0 || campaign.uncovered > 0) return 'Cannot Lend Staff';
  if (canLend > 0) return formatAgentCount(canLend);
  return 'None Available';
}

export function getCoverageReceivedLabel(count: number): string {
  return count > 0 ? formatAgentCount(count) : 'None';
}

export function getCoverageGivenLabel(count: number): string {
  return count > 0 ? formatAgentCount(count) : 'None';
}

export function getCampaignStaffingStatus(campaign: CampaignSummaryItem): {
  staffed: number;
  required: number;
  openPositions: number;
  statusLabel: string;
  needsCoverage: boolean;
  staffingPercent: number;
} {
  const required = campaign.scheduled;
  const staffed = Math.round((campaign.present + campaign.covered) * 100) / 100;
  const openPositions = Math.max(0, Math.round((required - staffed) * 100) / 100);
  const staffingPercent = required > 0 ? Math.min(100, Math.round((staffed / required) * 100)) : 0;

  let statusLabel = 'Fully Staffed';
  if (openPositions > 0 && staffed === 0) statusLabel = 'Coverage Needed';
  else if (openPositions > 0 && openPositions < 1) statusLabel = 'Partial Coverage Needed';
  else if (openPositions > 0) statusLabel = `${openPositions} Open Position${openPositions === 1 ? '' : 's'}`;

  return {
    staffed,
    required,
    openPositions,
    statusLabel,
    needsCoverage: openPositions > 0,
    staffingPercent,
  };
}

export function getCanLendCount(campaign: CampaignSummaryItem): number {
  const available = campaign.availableAgents ?? campaign.present;
  const internalNeed = Math.max(0, campaign.scheduled - (campaign.coverageReceived ?? 0));
  return Math.max(0, available - internalNeed);
}

export function getTotalLendableFromOthers(
  campaignId: string,
  campaigns: CampaignSummaryItem[],
): number {
  return campaigns
    .filter((c) => c.campaignId !== campaignId)
    .reduce((sum, c) => sum + getCanLendCount(c), 0);
}

const HEALTH_PRIORITY: Record<CampaignHealthLevel, number> = {
  critical: 0,
  warning: 1,
  monitor: 2,
  healthy: 3,
  not_scheduled: 4,
};

export function sortCampaignsByOperationalPriority(
  campaigns: CampaignSummaryItem[],
): CampaignSummaryItem[] {
  return [...campaigns].sort((a, b) => {
    const levelA = getCampaignHealthLevel(a.operationalScore, a.scheduled);
    const levelB = getCampaignHealthLevel(b.operationalScore, b.scheduled);
    const priorityDiff = HEALTH_PRIORITY[levelA] - HEALTH_PRIORITY[levelB];
    if (priorityDiff !== 0) return priorityDiff;

    const shortageA = getCampaignStaffingStatus(a).openPositions;
    const shortageB = getCampaignStaffingStatus(b).openPositions;
    if (shortageB !== shortageA) return shortageB - shortageA;

    return a.campaignName.localeCompare(b.campaignName);
  });
}

export interface CampaignCoverageActions {
  hasActiveCoverage: boolean;
  uncoveredRows: any[];
  coveredRows: any[];
  primaryCoveredRow: any | null;
}

export function getCampaignCoverageActions(campaignRows: any[] = []): CampaignCoverageActions {
  const coveredRows = campaignRows.filter(
    (row) =>
      ['COVERED', 'PARTIALLY_COVERED'].includes(row.coverageStatus)
      && row.coverageAssignmentId,
  );
  const uncoveredRows = campaignRows.filter(
    (row) => row.coverageStatus === 'NOT_COVERED' && row.requiresCoverage,
  );
  return {
    hasActiveCoverage: coveredRows.length > 0,
    uncoveredRows,
    coveredRows,
    primaryCoveredRow: coveredRows[0] || null,
  };
}

export interface RecommendedCampaignAction {
  label: string;
  actionable: boolean;
  actionType?: 'assign' | 'borrow' | 'monitor' | 'change' | 'remove';
}

export function getRecommendedCampaignAction(
  campaign: CampaignSummaryItem,
  allCampaigns: CampaignSummaryItem[],
  campaignRows: any[] = [],
): RecommendedCampaignAction {
  if (campaign.scheduled <= 0) {
    return { label: 'Not Scheduled Today', actionable: false };
  }

  const staffing = getCampaignStaffingStatus(campaign);
  const health = getCampaignHealthLevel(campaign.operationalScore, campaign.scheduled);
  const { hasActiveCoverage, uncoveredRows } = getCampaignCoverageActions(campaignRows);

  if (hasActiveCoverage) {
    if (staffing.openPositions === 0) {
      return { label: 'Coverage Active', actionable: false };
    }
    const hasPartial = campaignRows.some((r) => r.coverageStatus === 'PARTIALLY_COVERED');
    if (hasPartial) {
      return { label: 'Add Coverage', actionable: true, actionType: 'assign' };
    }
    return { label: 'Change Coverage', actionable: true, actionType: 'change' };
  }

  if (staffing.openPositions === 0 && uncoveredRows.length === 0) {
    return { label: 'Fully Staffed', actionable: false };
  }

  const lendableElsewhere = getTotalLendableFromOthers(campaign.campaignId, allCampaigns);
  const borrowCount = Math.min(staffing.openPositions, lendableElsewhere);

  if (borrowCount >= 1 && uncoveredRows.length === 0) {
    const agents = Math.ceil(borrowCount);
    return {
      label: agents === 1 ? 'Borrow 1 Agent' : `Borrow ${agents} Agents`,
      actionable: true,
      actionType: 'borrow',
    };
  }

  if (health === 'monitor' && uncoveredRows.length === 0) {
    return { label: 'Monitor', actionable: false, actionType: 'monitor' };
  }

  if (uncoveredRows.length > 0 && staffing.openPositions <= 1) {
    return { label: 'Assign Coverage', actionable: true, actionType: 'assign' };
  }

  if (uncoveredRows.length > 0) {
    const agents = Math.ceil(staffing.openPositions);
    return {
      label: `Assign ${agents} Agents`,
      actionable: true,
      actionType: 'assign',
    };
  }

  if (staffing.openPositions > 0) {
    return { label: 'Change Coverage', actionable: true, actionType: 'change' };
  }

  return { label: 'Fully Staffed', actionable: false };
}

export function getCampaignHealthDisplay(campaign: CampaignSummaryItem): {
  level: CampaignHealthLevel;
  label: string;
  emoji: string;
} {
  const level = getCampaignHealthLevel(campaign.operationalScore, campaign.scheduled);
  return {
    level,
    label: getCampaignHealthLabel(level),
    emoji: level === 'not_scheduled' ? '⚪' : getOperationalScoreEmoji(campaign.operationalScore),
  };
}

export function buildActionQueue(rows: any[], campaignSummary: CampaignSummaryItem[]): ActionQueueItem[] {
  const items: ActionQueueItem[] = [];

  rows.forEach((row) => {
    if (row.coverageStatus === 'NOT_COVERED' && row.requiresCoverage) {
      items.push({
        id: `assign-${row.id}`,
        priority: 'high',
        title: `Assign coverage for ${row.agentName}`,
        campaignName: row.campaignName,
        campaignId: row.campaignId,
        rowId: row.id,
        actionType: 'assign_coverage',
      });
    } else if (row.absenceReason === 'PENDING_SUPERVISOR_REVIEW' && row.requiresCoverage) {
      items.push({
        id: `absence-${row.id}`,
        priority: 'medium',
        title: `Review absence reason for ${row.agentName}`,
        campaignName: row.campaignName,
        campaignId: row.campaignId,
        rowId: row.id,
        actionType: 'review_absence',
      });
    } else if (
      row.coverageStatus === 'NOT_COVERED'
      && row.coverageAssignmentId
      && row.attendanceStatus !== 'SCHEDULED_OFF'
    ) {
      items.push({
        id: `pending-${row.id}`,
        priority: 'medium',
        title: `Coverage assignment pending for ${row.agentName}`,
        campaignName: row.campaignName,
        campaignId: row.campaignId,
        rowId: row.id,
        actionType: 'coverage_pending',
      });
    }
  });

  campaignSummary.forEach((campaign) => {
    if (campaign.operationalScore < 75 && campaign.scheduled > 0) {
      items.push({
        id: `threshold-${campaign.campaignId}`,
        priority: campaign.operationalScore < 90 ? 'high' : 'medium',
        title: `${campaign.campaignName} below staffing threshold`,
        campaignName: campaign.campaignName,
        campaignId: campaign.campaignId,
        actionType: 'staffing_threshold',
      });
    }
  });

  const priorityOrder = { high: 0, medium: 1, low: 2 };
  return items.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);
}

export function getCoverageLoadTooltip(summary: {
  coverageLoad?: string;
  coverageLoadWarnings?: string[];
}): string {
  const warnings = summary.coverageLoadWarnings || [];
  if (warnings.length > 0) return warnings.join('\n');

  const load = summary.coverageLoad || 'Normal';
  if (load === 'Critical') {
    return 'At least one agent is covering 3 or more absence assignments today.';
  }
  if (load === 'Elevated') {
    return 'At least one agent is covering 2 absence assignments today.';
  }
  return 'No covering agent is assigned to more than one absence today.';
}

export function formatShiftTime(start?: string | null, end?: string | null): string {
  if (!start || !end) return '-';
  const fmt = (t: string) => {
    const [h, m] = t.split(':').map(Number);
    const hour = h % 12 || 12;
    const ampm = h >= 12 ? 'PM' : 'AM';
    return `${hour}:${String(m).padStart(2, '0')} ${ampm}`;
  };
  return `${fmt(start)} - ${fmt(end)}`;
}

export { OPS_COLORS };
